/**
 * The stores list and a store's page. A store's open-now line comes from the hub's hours when it has them, otherwise
 * from the store's own published hours (Google's, copied nightly onto the Store metaobject), worked out in the store's
 * own time zone so a Perth store reads right from Sydney. With neither, there's no line, never a guess. Search, Near
 * me, the state chips, "Not near a Messina yet?" (the Messina Stores app at /apps/stores) and Get notified for a store
 * not open yet are ported from the live theme's messina-stores.js.
 */
import { hub, setStoreChoice } from "./ms-hub.js";

const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DOW_LONG = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** The day, minutes past midnight and date in a time zone. */
export function nowIn(tz, at = new Date()) {
  let parts;
  const o = { weekday: "short", year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" };
  try { parts = new Intl.DateTimeFormat("en-US", { timeZone: tz, ...o }).formatToParts(at); } catch { parts = new Intl.DateTimeFormat("en-US", o).formatToParts(at); }
  const g = (t) => parts.find((p) => p.type === t)?.value;
  return { day: DOW.indexOf(g("weekday")), mins: (+g("hour") % 24) * 60 + +g("minute"), date: `${g("year")}-${g("month")}-${g("day")}` };
}

export function addDays(iso, n) {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** 1350 → "10:30pm". */
export function fmt(mins) {
  const m = ((mins % 1440) + 1440) % 1440;
  const h = Math.floor(m / 60), mm = m % 60;
  return `${h % 12 === 0 ? 12 : h % 12}${mm ? `:${String(mm).padStart(2, "0")}` : ""}${h < 12 ? "am" : "pm"}`;
}
const hhmm = (s) => { const [h, m] = String(s).split(":").map(Number); return h * 60 + (m || 0); };

/** [open, close] minute ranges for one date, confirmed holiday hours first. Periods are Google's: [day, mins, day, mins], Sunday 0. */
function dayPeriods(s, date, dow) {
  const sp = (s.special || []).find((x) => x.date === date && x.confirmed);
  if (sp) return (sp.periods || []).map(([o, c]) => [o, c]);
  return (s.periods || []).filter((p) => p[0] === dow).map(([od, om, cd, cm]) => [om, ((cd - od + 7) % 7) * 1440 + cm]);
}

function fromHub(h, n) {
  if (!h || h.openNow == null) return null;
  const t = h.today;
  if (h.openNow && t && t.closes) return { tone: "good", text: `Open now · closes ${fmt(hhmm(t.closes))}` };
  if (h.openNow) return { tone: "good", text: "Open now" };
  if (t && !t.closed && t.opens && hhmm(t.opens) > n.mins) return { tone: "", text: `Opens ${fmt(hhmm(t.opens))}` };
  if (h.next) {
    const days = Math.round((Date.parse(`${h.next.date}T12:00:00Z`) - Date.parse(`${n.date}T12:00:00Z`)) / 864e5);
    const day = DOW_LONG[new Date(`${h.next.date}T12:00:00Z`).getUTCDay()];
    return { tone: "", text: `Closed · opens ${days === 1 ? "tomorrow" : day} ${fmt(hhmm(h.next.opens))}` };
  }
  return { tone: "", text: "Closed" };
}

/**
 * A store's line: { tone, text } or null when its hours can't be read. s: { status, tz, periods, special, hub? } where
 * hub is the hub's { today, openNow, next } for it.
 */
export function statusOf(s, at = new Date()) {
  if (s.status === "coming_soon") return { tone: "", text: "Opening soon" };
  if (s.status === "temporarily_closed") return { tone: "", text: "Temporarily closed" };
  if (s.status === "closed") return { tone: "", text: "Closed" };
  const n = nowIn(s.tz || "Australia/Sydney", at);
  const live = fromHub(s.hub, n);
  if (live) return live;
  if (!s.periods || !s.periods.length) return null;
  const open = (close) => ({ tone: "good", text: close - n.mins >= 1440 ? "Open 24 hours" : `Open now · closes ${fmt(close)}` });
  for (const [, c] of dayPeriods(s, addDays(n.date, -1), (n.day + 6) % 7)) if (c > 1440 && n.mins < c - 1440) return open(c - 1440);
  for (const [o, c] of dayPeriods(s, n.date, n.day)) if (n.mins >= o && n.mins < c) return open(c);
  for (let i = 0; i < 8; i++) {
    const dow = (n.day + i) % 7;
    const next = dayPeriods(s, addDays(n.date, i), dow).filter(([o]) => i > 0 || o > n.mins).sort((a, b) => a[0] - b[0])[0];
    if (next) return { tone: "", text: i === 0 ? `Opens ${fmt(next[0])}` : i === 1 ? `Closed · opens tomorrow ${fmt(next[0])}` : `Closed · opens ${DOW_LONG[dow]} ${fmt(next[0])}` };
  }
  return { tone: "", text: "Closed" };
}

export function km(a, b) {
  const R = 6371, r = (x) => (x * Math.PI) / 180;
  const h = Math.sin(r(b.lat - a.lat) / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(r(b.lng - a.lng) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
export const kmText = (d) => (d < 10 ? `${d.toFixed(1)} km away` : `${Math.round(d).toLocaleString("en-AU")} km away`);
const norm = (s) => (s || "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "");
const bare = (s) => norm(s).replace(/\bmessina\b/g, "").replace(/[^a-z0-9]+/g, " ").trim();

/** The hub's store for a Store metaobject: its own hub id when set, otherwise the same name (without "Messina"). */
export function hubStoreFor(hubStores, store) {
  if (!Array.isArray(hubStores)) return null;
  if (store.hubId) return hubStores.find((h) => String(h.id) === String(store.hubId)) || null;
  const want = bare(store.name);
  return want ? hubStores.find((h) => bare(h.name) === want) || null : null;
}

/** Order from this store: the Order page at it, and the store choice to save (null when the hub doesn't know it). */
export function orderTarget(hubStores, store) {
  const h = hubStoreFor(hubStores, store);
  const id = h?.id ?? store.hubId;
  return { href: id ? `/pages/order?store=${encodeURIComponent(id)}` : "/pages/order", choice: h ? { id: String(h.id), name: h.name, state: h.state } : null };
}

// ── The browser (custom elements) ─────────────────────────────────────────────────────────────────────────────────

const parse = (s, fallback) => { try { return JSON.parse(s) ?? fallback; } catch { return fallback; } };
const storeOf = (el) => ({ status: el.dataset.status || "open", tz: el.dataset.tz, periods: parse(el.dataset.periods, []), special: parse(el.dataset.special, []), name: el.dataset.name, hubId: el.dataset.hubStore || "" });
const esc = (s) => String(s == null ? "" : s).replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));

function paintStatus(el, hubStores) {
  const out = el.querySelector("[data-store-status]");
  if (!out) return;
  const s = storeOf(el);
  const h = hubStoreFor(hubStores, s);
  const st = statusOf({ ...s, hub: h?.hours });
  out.innerHTML = st ? `<span class="status${st.tone ? ` ${st.tone}` : ""}">${esc(st.text)}</span>` : "";
}

// Every store's hours from the hub, read once a page.
let hubRead;
const hubStores = () => (hubRead ||= hub("stores", {}).then((r) => (r.ok && Array.isArray(r.data?.stores) ? r.data.stores : Array.isArray(r.data) ? r.data : null)));

const NAME = /^[\p{L}\p{M}][\p{L}\p{M}' .\-’]{0,39}$/u;
const EMAIL = /^[a-z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?(?:\.[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?)+$/i;
const DOMAINS = ["gmail.com", "hotmail.com", "outlook.com", "yahoo.com", "yahoo.com.au", "icloud.com", "me.com", "live.com", "live.com.au", "bigpond.com", "bigpond.net.au", "optusnet.com.au", "hotmail.com.au", "outlook.com.au", "protonmail.com"];
function lev(a, b) {
  const d = Array.from({ length: a.length + 1 }, (_, i) => [i]);
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++) for (let j = 1; j <= b.length; j++) d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return d[a.length][b.length];
}
/** A likely typo in a common email domain (gmial.com for gmail.com), as the fix to offer. */
export function typoFix(email) {
  const [user, domain] = email.toLowerCase().split("@");
  if (!domain || DOMAINS.includes(domain)) return null;
  const best = DOMAINS.map((d) => [d, lev(domain, d)]).sort((a, b) => a[1] - b[1])[0];
  return best && best[1] > 0 && best[1] <= 2 ? `${user}@${best[0]}` : null;
}
const uuid = () => (globalThis.crypto?.randomUUID ? crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`);

function formBits(form) {
  const f = (n) => form.elements[n];
  // An error line under a field: the form's own, or one made under the field the first time it's needed.
  const errEl = (name) => {
    let el = form.querySelector(`[data-error="${name}"]`);
    if (el) return el;
    const input = f(name === "place" ? "suburb" : name);
    const field = input?.closest?.(".field");
    if (!field) return null;
    el = document.createElement("p");
    el.className = "err";
    el.dataset.error = name;
    el.id = `${input.id}-err`;
    el.hidden = true;
    field.append(el);
    input.setAttribute("aria-describedby", el.id);
    return el;
  };
  const setError = (name, msg, html) => {
    const el = errEl(name), input = f(name === "place" ? "suburb" : name);
    if (el) { if (html) el.innerHTML = html; else el.textContent = msg || ""; el.hidden = !msg && !html; }
    if (input?.setAttribute) input.setAttribute("aria-invalid", msg || html ? "true" : "false");
  };
  const checkPerson = () => {
    const first = f("first_name").value.trim().replace(/\s+/g, " ");
    const email = f("email").value.trim();
    let bad = false;
    if (!first) { setError("first_name", "Add your first name."); bad = true; }
    else if (!NAME.test(first)) { setError("first_name", "Use letters only for your name."); bad = true; }
    else setError("first_name", "");
    if (!email) { setError("email", "Add your email."); bad = true; }
    else if (!EMAIL.test(email)) { setError("email", "That email doesn’t look right."); bad = true; }
    else setError("email", "");
    return { first, email, bad };
  };
  const say = (text) => { const e = form.querySelector("[data-form-error]"); if (e) { e.textContent = text || ""; e.hidden = !text; } };
  return { f, errEl, setError, checkPerson, say };
}

async function send(form, path, body, busyWords) {
  const btn = form.querySelector("[type=submit]");
  const label = btn.innerHTML;
  btn.disabled = true;
  btn.textContent = busyWords;
  try {
    const r = await fetch(`${form.dataset.endpoint}/${path}`, { method: "POST", headers: { "content-type": "application/json", accept: "application/json" }, body: JSON.stringify(body) });
    const data = await r.json().catch(() => ({}));
    return { ok: r.ok && data.ok, data };
  } catch {
    return { ok: false, data: { error: "We couldn’t send that. Check your connection and try again." } };
  } finally {
    btn.disabled = false;
    btn.innerHTML = label;
  }
}

function showDone(form, title, text) {
  const done = form.querySelector("[data-done]");
  done.querySelector("[data-done-title]").textContent = title;
  done.querySelector("[data-done-text]").textContent = text;
  form.querySelector("[data-fields]").hidden = true;
  done.hidden = false;
  done.focus();
}

function initSuggest(form) {
  const { f, errEl, setError, checkPerson, say } = formBits(form);
  const sub = f("suburb"), placeId = f("place_id"), list = form.querySelector("[data-places]");
  let session = uuid(), options = [], active = -1, timer, req = 0;
  const close = () => { list.hidden = true; sub.setAttribute("aria-expanded", "false"); sub.removeAttribute("aria-activedescendant"); active = -1; };
  const highlight = (i) => { active = i; [...list.querySelectorAll("[role=option]")].forEach((li, k) => li.setAttribute("aria-selected", String(k === i))); if (i >= 0) sub.setAttribute("aria-activedescendant", `st-opt-${i}`); };
  const choose = (i) => { const o = options[i]; if (!o) return; sub.value = o.secondary ? `${o.main}, ${o.secondary}` : o.main; placeId.value = o.id; setError("place", ""); close(); };
  const render = () => {
    list.innerHTML = (options.length ? "" : '<li class="pick-empty">No matches yet. Try the suburb or town name.</li>')
      + options.map((o, i) => `<li id="st-opt-${i}" role="option" aria-selected="false" data-i="${i}"><b>${esc(o.main)}</b><span>${esc(o.secondary)}</span></li>`).join("")
      + '<li class="pick-credit" aria-hidden="true">Google Maps</li>';
    list.hidden = false;
    sub.setAttribute("aria-expanded", "true");
  };
  list.addEventListener("mousedown", (e) => { const li = e.target.closest("[data-i]"); if (li) { e.preventDefault(); choose(+li.dataset.i); } });
  const lookup = async () => {
    const q = sub.value.trim();
    if (q.length < 2) return close();
    const mine = ++req;
    try {
      const r = await fetch(`${form.dataset.endpoint}/places?q=${encodeURIComponent(q)}&s=${encodeURIComponent(session)}`, { headers: { accept: "application/json" } });
      const data = await r.json();
      if (mine !== req) return;
      options = data.suggestions || [];
      render();
    } catch { if (mine === req) close(); }
  };
  sub.addEventListener("input", () => { placeId.value = ""; clearTimeout(timer); timer = setTimeout(lookup, 220); });
  sub.addEventListener("keydown", (e) => {
    if (list.hidden) return;
    if (e.key === "ArrowDown") { e.preventDefault(); highlight(Math.min(active + 1, options.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); highlight(Math.max(active - 1, 0)); }
    else if (e.key === "Enter" && active >= 0) { e.preventDefault(); choose(active); }
    else if (e.key === "Escape") close();
  });
  sub.addEventListener("blur", () => setTimeout(close, 120));
  f("email").addEventListener("blur", () => {
    const v = f("email").value.trim();
    const fix = v && EMAIL.test(v) && typoFix(v);
    if (fix) {
      setError("email", "", `Did you mean <button type="button" class="t-link">${esc(fix)}</button>?`);
      errEl("email").querySelector("button").addEventListener("click", () => { f("email").value = fix; setError("email", ""); });
    }
  });
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    say("");
    const { first, email, bad: badPerson } = checkPerson();
    let bad = badPerson;
    if (!placeId.value) { setError("place", sub.value.trim() ? "Pick a suburb from the list." : "Add a suburb or town."); bad = true; }
    if (bad) return form.querySelector('[aria-invalid="true"]')?.focus();
    const r = await send(form, "suggest", { first_name: first, email, place_id: placeId.value, session, company: f("company").value }, "Sending…");
    if (r.ok) { showDone(form, `Thanks, ${r.data.first_name || first}.`, `${r.data.label || sub.value} has your vote.`); session = uuid(); return; }
    if (r.data.errors) { Object.entries(r.data.errors).forEach(([k, v]) => setError(k, v)); form.querySelector('[aria-invalid="true"]')?.focus(); }
    else say(r.data.error || "Something went wrong on our end. Try again in a minute.");
  });
}

/** The store openings form's stores: every ticked box, or the one store the page is about. */
export function pickedStores(inputs) {
  return [...inputs].filter((i) => i.type === "hidden" || i.checked).map((i) => i.value);
}

function initOpenings(form) {
  const { f, setError, checkPerson, say } = formBits(form);
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    say("");
    const boxes = form.querySelectorAll('input[name="stores"]');
    const stores = pickedStores(boxes);
    const pickErr = form.querySelector('[data-error="stores"]');
    if (pickErr) { pickErr.textContent = stores.length ? "" : "Pick at least one store."; pickErr.hidden = !!stores.length; }
    const { first, email, bad } = checkPerson();
    const fix = !bad && typoFix(email);
    if (fix && fix !== email) { setError("email", `Did you mean ${fix}?`); f("email").value = fix; return f("email").focus(); }
    if (bad || !stores.length) return form.querySelector('[aria-invalid="true"], input[name="stores"]')?.focus();
    const names = [...boxes].filter((b) => stores.includes(b.value)).map((b) => b.dataset.name);
    const r = await send(form, "notify", { first_name: first, email, stores, company: f("company").value }, "Signing you up…");
    if (r.ok) { showDone(form, `You’re on the list, ${r.data.first_name || first}.`, `We’ll email you when ${names.length === 1 ? names[0] : "they"} open${names.length === 1 ? "s" : ""}.`); return; }
    if (r.data.errors) { Object.entries(r.data.errors).forEach(([k, v]) => setError(k, v)); form.querySelector('[aria-invalid="true"]')?.focus(); }
    else say(r.data.error || "Something went wrong on our end. Try again in a minute.");
  });
}

function initNotify(sheet) {
  const form = sheet.querySelector("form");
  const { f, setError, checkPerson, say } = formBits(form);
  let chosen = null;
  document.addEventListener("click", (e) => {
    const b = e.target.closest("[data-notify]");
    if (!b) return;
    chosen = { handle: b.dataset.notify, name: b.dataset.notifyName || "this store" };
    sheet.querySelector("[data-notify-store]").textContent = chosen.name;
    ["first_name", "email"].forEach((n) => setError(n, ""));
    say("");
    form.querySelector("[data-fields]").hidden = false;
    form.querySelector("[data-done]").hidden = true;
  });
  form.addEventListener("submit", async (e) => {
    e.preventDefault();
    say("");
    const { first, email, bad } = checkPerson();
    const fix = !bad && typoFix(email);
    if (fix && fix !== email) { setError("email", `Did you mean ${fix}?`); f("email").value = fix; return f("email").focus(); }
    if (bad) return form.querySelector('[aria-invalid="true"]')?.focus();
    const r = await send(form, "notify", { first_name: first, email, stores: [chosen?.handle].filter(Boolean), company: f("company").value }, "Signing you up…");
    if (r.ok) { showDone(form, `You’re on the list, ${r.data.first_name || first}.`, `We’ll email you when ${r.data.store || chosen?.name} opens.`); f("first_name").value = ""; f("email").value = ""; return; }
    if (r.data.errors) { Object.entries(r.data.errors).forEach(([k, v]) => setError(k, v)); form.querySelector('[aria-invalid="true"]')?.focus(); }
    else say(r.data.error || "Something went wrong on our end. Try again in a minute.");
  });
}

if (typeof window !== "undefined" && typeof customElements !== "undefined") {
  /** The stores list: chips, search, Near me, folded regions on a phone. Reads ?state=, ?q= and ?near=me from the home's finder. */
  class MsStores extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.cards = [...this.querySelectorAll("[data-store]")];
      this.regions = [...this.querySelectorAll(".st-region")];
      this.state = { region: "all", q: "", here: null };
      const q = new URLSearchParams(location.search);
      this.search = this.querySelector("[data-store-search]");
      this.note = this.querySelector("[data-store-note]");
      if (q.get("state")) this.state.region = q.get("state");
      if (q.get("q") && this.search) { this.search.value = q.get("q"); this.state.q = q.get("q"); }
      this.addEventListener("click", (e) => this.onClick(e));
      let t;
      this.search?.addEventListener("input", () => { clearTimeout(t); t = setTimeout(() => { this.state.q = this.search.value; this.apply(); }, 120); });
      this.querySelector("form[role=search]")?.addEventListener("submit", (e) => { e.preventDefault(); this.state.q = this.search.value; this.apply(); });
      this.apply();
      if (q.get("near") === "me") this.near();
      this.cards.forEach((c) => paintStatus(c, null));
      hubStores().then((hs) => { if (hs) this.cards.forEach((c) => paintStatus(c, hs)); });
    }

    onClick(e) {
      const chip = e.target.closest("[data-state]");
      if (chip) { e.preventDefault(); this.state.region = chip.dataset.state; this.apply(); history.replaceState(history.state, "", chip.getAttribute("href")); return; }
      if (e.target.closest("[data-near-clear]")) { if (this.search) this.search.value = ""; this.state.q = ""; if (!this.state.here) this.near(); else this.apply(); return; }
      if (e.target.closest("[data-near]")) { this.near(); return; }
      const fold = e.target.closest("[data-fold]");
      if (fold) { const r = fold.closest(".st-region"); const open = r.classList.toggle("unfolded"); fold.setAttribute("aria-expanded", String(open)); }
    }

    near() {
      const btn = this.querySelector("[data-near]");
      if (this.state.here) { this.state.here = null; btn?.setAttribute("aria-pressed", "false"); return this.apply(); }
      if (!navigator.geolocation) { this.say("Your browser can’t share where you are. Search by suburb or postcode instead."); return; }
      if (btn) btn.disabled = true;
      navigator.geolocation.getCurrentPosition(
        (pos) => { if (btn) { btn.disabled = false; btn.setAttribute("aria-pressed", "true"); } this.state.here = { lat: pos.coords.latitude, lng: pos.coords.longitude }; this.say(""); this.apply(); this.querySelector("[data-nearest]")?.scrollIntoView({ behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth", block: "start" }); },
        () => { if (btn) btn.disabled = false; this.say("We couldn’t get your location. Search by suburb or postcode instead."); },
        { enableHighAccuracy: false, timeout: 10000, maximumAge: 600000 },
      );
    }

    say(text) { if (this.note) { this.note.textContent = text; this.note.hidden = !text; } }

    apply() {
      const { region, q, here } = this.state;
      const tokens = norm(q).split(/\s+/).filter(Boolean);
      const matches = (c) => (region === "all" || c.dataset.region === region) && tokens.every((t) => norm(c.dataset.search).includes(t));
      this.querySelectorAll("[data-state]").forEach((c) => { const on = c.dataset.state === region; c.classList.toggle("on", on); if (on) c.setAttribute("aria-current", "true"); else c.removeAttribute("aria-current"); });
      const nearest = this.querySelector("[data-nearest]");
      let shown = 0;
      if (here && nearest) {
        this.regions.forEach((r) => { r.hidden = true; });
        const grid = nearest.querySelector(".row");
        grid.replaceChildren();
        this.cards.filter(matches).map((c) => ({ c, d: km(here, { lat: +c.dataset.lat, lng: +c.dataset.lng }) }))
          .filter((x) => Number.isFinite(x.d)).sort((a, b) => a.d - b.d).slice(0, 8)
          .forEach(({ c, d }) => { const k = c.cloneNode(true); const dist = k.querySelector("[data-distance]"); if (dist) { dist.textContent = kmText(d); dist.hidden = false; } grid.append(k); shown++; });
        nearest.hidden = shown === 0;
      } else {
        if (nearest) nearest.hidden = true;
        const filtering = region !== "all" || tokens.length > 0;
        this.regions.forEach((r) => {
          let n = 0;
          r.querySelectorAll("[data-store]").forEach((c) => { const ok = matches(c); c.hidden = !ok; if (ok) n++; });
          r.hidden = n === 0;
          // Searching or picking a state opens what it found, on a phone too.
          r.classList.toggle("found", filtering && n > 0);
          shown += n;
        });
      }
      const empty = this.querySelector("[data-store-empty]");
      if (empty) { empty.hidden = shown > 0; const w = empty.querySelector("[data-empty-q]"); if (w) w.textContent = q.trim(); }
    }
  }
  if (!customElements.get("ms-stores")) customElements.define("ms-stores", MsStores);

  /** A store's page: its line, today's hours, Order from it, and how far the nearby stores are. */
  class MsStorePage extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      const s = storeOf(this);
      const today = nowIn(s.tz || "Australia/Sydney");
      this.querySelectorAll("[data-dow]").forEach((row) => {
        const on = Number(row.dataset.dow) === today.day;
        row.classList.toggle("today", on);
        const tag = row.querySelector("[data-today]");
        if (tag) tag.hidden = !on;
      });
      this.querySelectorAll("[data-date]").forEach((el) => { if (el.dataset.date < today.date || el.dataset.date > addDays(today.date, 6)) el.remove(); });
      const here = { lat: +this.dataset.lat, lng: +this.dataset.lng };
      const near = [...this.querySelectorAll("[data-store]")];
      near.forEach((c) => {
        const d = km(here, { lat: +c.dataset.lat, lng: +c.dataset.lng });
        const out = c.querySelector("[data-distance]");
        if (out && Number.isFinite(d)) { out.textContent = kmText(d); out.hidden = false; }
      });
      // A Nearby section's wrapper is no store of its own: only the cards get a line there.
      const own = this.dataset.status ? [this] : [];
      [...own, ...near].forEach((el) => paintStatus(el, null));
      const order = this.querySelector("[data-order-from]");
      hubStores().then((hs) => {
        if (hs) [...own, ...near].forEach((el) => paintStatus(el, hs));
        if (order) { const t = orderTarget(hs, s); order.href = t.href; this.choice = t.choice; }
      });
      order?.addEventListener("click", () => { if (this.choice) setStoreChoice(this.choice); });
    }
  }
  if (!customElements.get("ms-store-page")) customElements.define("ms-store-page", MsStorePage);

  document.querySelectorAll("form[data-suggest]").forEach(initSuggest);
  document.querySelectorAll("[data-notify-sheet]").forEach(initNotify);
  document.querySelectorAll("form[data-openings]").forEach(initOpenings);
}
